// Agent Portal (/cti2) — "please stay on this page" notice, mounted once next
// to ExitGuard in AgentPortalPage.tsx. All the guarding lives in
// useReloadGuard; this only renders the dialog. No leave option on purpose.
import { useRef } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCallStore, isCallActive } from '@/router/zustand/callStore';
import { useReloadGuard } from '../hooks/useReloadGuard';

export default function ReloadGuardDialog() {
  const { open, stay } = useReloadGuard();
  // Live: a mobile-answered call can go active while the dialog is open.
  const callActive = useCallStore(isCallActive);
  const stayRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) stay(); }}>
      <DialogContent
        role="alertdialog"
        className="z-[1100] max-w-md"
        overlayClassName="z-[1090]"
        onOpenAutoFocus={(e) => { e.preventDefault(); stayRef.current?.focus(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive" aria-hidden="true">
              <FiRefreshCw size={16} />
            </span>
            Please stay on this page
          </DialogTitle>
        </DialogHeader>
        {callActive && (
          <p className="text-sm font-semibold text-destructive" role="alert">
            You're on a call right now. Leaving this page would disconnect it.
          </p>
        )}
        <DialogDescription asChild>
          <div className="space-y-2">
            <p>
              You're logged in to a calling profile, so this page can't be reloaded or closed
              right now. Doing so would disconnect your softphone, and it can take a while for
              WebRTC to reconnect. Ongoing and incoming calls, including those on mobile, may be
              dropped in the meantime.
            </p>
            <p>If you need to reload, please log out of your profile first. Thank you for understanding.</p>
          </div>
        </DialogDescription>
        <DialogFooter>
          <Button ref={stayRef} onClick={stay}>OK, I'll stay</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
